import { emailRegex } from 'lib/constants'
import * as _ from 'lodash'

export const commaFormat = number => {
  if (number === 0) { return number }
  if (!number) { return '' }
  return parseInt(number, 10).toLocaleString('en-US')
}

/**
 * Convert Western numbers (0-9) to Eastern Arabic numerals (٠-٩)
 * Used for displaying numbers in Arabic locale
 */
export const toArabicNumerals = num => {
  if (num === null || num === undefined) return ''
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
  return String(num).replace(/\d/g, digit => arabicNumerals[digit])
}

/**
 * Format number with locale-aware grouping and numerals (e.g. 1,234 in en-US).
 * For Arabic locale, uses grouping then Eastern Arabic digits.
 */
export const formatLocaleNumber = (num, locale) => {
  if (num === null || num === undefined) return ''
  const n = Number(num)
  if (Number.isNaN(n)) return String(num)
  const loc = locale && String(locale).length ? locale : 'en-US'
  if (locale?.startsWith('ar')) {
    return toArabicNumerals(n.toLocaleString('en-US'))
  }
  return n.toLocaleString(loc)
}

/**
 * Formats a dollar amount with two decimals and locale-aware grouping (e.g. $1,234.50).
 * @param amount a number or numeric string
 * @param locale the i18n language; `ar*` renders Eastern Arabic digits
 * @returns the formatted amount, '' for null/undefined/empty, or the raw value if not numeric
 */
export const formatCurrency = (amount, locale?: string) => {
  if (amount === null || amount === undefined || amount === '') return ''
  const n = Number(amount)
  if (Number.isNaN(n)) return String(amount)
  const isArabic = Boolean(locale?.startsWith('ar'))
  const loc = !locale || isArabic ? 'en-US' : locale
  const formatted = n.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `$${isArabic ? toArabicNumerals(formatted) : formatted}`
}

/**
 * Like `formatCurrency`, but abbreviates amounts of 1,000 or more (e.g. $1.5k).
 * @param amount a number or numeric string
 * @param locale the i18n language
 * @returns the formatted amount
 */
export const formatCompactCurrency = (amount, locale?: string) => {
  const n = Number(amount)
  if (!(Math.abs(n) >= 1000)) return formatCurrency(amount, locale)
  const compact = `$${n < 0 ? '-' : ''}${formatLongNumber(Math.abs(n))}`
  return locale?.startsWith('ar') ? toArabicNumerals(compact) : compact
}

export const isEmptyObject = obj => (
  !!obj && Object.keys(obj).length === 0 && obj.constructor === Object
)

export const getDayBefore = (dateStr = undefined) => {
  const date = dateStr ? new Date(dateStr) : new Date()
  date.setDate(date.getDate() - 1)
  return date
}

export const getWeekBefore = (dateStr = undefined) => {
  const date = dateStr ? new Date(dateStr) : new Date()
  date.setDate(date.getDate() - 7)
  return date
}

export const get30DaysBefore = (dateStr = undefined) => {
  const date = dateStr ? new Date(dateStr) : new Date()
  date.setDate(date.getDate() - 30)
  return date
}

export const getThreeMonthsBefore = (dateStr = undefined) => {
  const date = dateStr ? new Date(dateStr) : new Date()
  date.setMonth(date.getMonth() - 3)
  return date
}

export const getOneYearBefore = (dateStr = undefined) => {
  const date = dateStr ? new Date(dateStr) : new Date()
  date.setFullYear(date.getFullYear() - 1)
  return date
}

export const getYearToDate = (dateStr = undefined) => {
  const date = dateStr ? new Date(dateStr) : new Date()
  date.setMonth(0)
  date.setDate(1)
  return date
}

export const getTextForCircle = (fullName: string, email?: string) => {
  if (fullName) {
    return fullName
      .split(' ')
      .slice(0, 2)
      .filter(word => word.length > 0)
      .map(word => word[0].toUpperCase())
      .join('')
  }

  return email?.slice(0, 2).toUpperCase() ?? ''
}

export const splitEmail = value => {
  const parts = value.split(/(@)/)
  return {
    username: parts[0],
    domain: `@${parts[2]}`,
  }
}

// output Feb 28, 2022, 9:59 PM
export const formatMediumDateShortTime = datetime => {
  const date = new Date(datetime)
  return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
}

// output March 19, 2025, 10:00 PM
export const longFormatDate = isoString => {
  const date = new Date(isoString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

// output Apr 25, 3:28 PM
export const shortDateTimeNoYear = datetime => {
  const date = new Date(datetime)
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric',
  })
}

// output Apr 26, 2022
export const formatShortDate = datetime => {
  const date = new Date(datetime)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// output 2:25 PM EDT
export const formatShortTime = datetime => {
  const date = new Date(datetime)
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' })
}

// output YYYY-MM-DD
export const formatYYYYMMDD = dateStr => {
  const date = new Date(dateStr)
  return date.toISOString().split('T')[0]
}

/*
 * Convert a decimal into percentage
 * @param {Number}
 * @return {String}
 */
export const percentFormat = decimal => `${Math.ceil(decimal * 100)}%`

/*
 * Download a file represented as a blob
 *
 * @param {Response.blob}
 * @param {String} source url
 * @param {String} name of file
 */
export const downloadBlob = (blob, filename = '') => {
  // Create blob link to download
  const d = new Date()
  const dateSuffix = `${d.getMonth() + 1}_${d.getDate()}_${d.getFullYear()}`
  const url = window.URL.createObjectURL(new Blob([blob]))
  const link = document.createElement('a')
  link.href = url
  let fName
  if (filename !== '') {
    if (filename.includes('.csv')) {
      const parts = filename.split('.')
      fName = `${parts[0]}_${dateSuffix}.csv`
    } else {
      fName = `${filename}_${dateSuffix}.csv`
    }
  } else {
    fName = `verifications_${dateSuffix}.csv`
  }
  link.setAttribute('download', fName)
  // Append to html page
  document.body.appendChild(link)
  // Force download
  link.click()
  // Clean up and remove the link
  link.parentNode.removeChild(link)
}

export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@.#$!%*?&^])[A-Za-z\d@.#$!%*?&_-]{12,100}$/
export const LOGIN_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@.#$!%*?&^])[A-Za-z\d@.#$!%*?&_-]{8,100}$/
export const isPasswordValid = string => PASSWORD_REGEX.test(string)
export const isLoginPasswordValid = string => LOGIN_PASSWORD_REGEX.test(string)

export const isEmail = string => emailRegex.test(string)

export const copyToClipboard = info => {
  navigator.clipboard.writeText(info)
}

export const capitalizeFirstLetter = string => {
  const split = string.split(' ')
  const capitalized = split.map(s => s.slice(0, 1).toUpperCase() + s.slice(1))
  return capitalized.join(' ')
}

// input: 'HELLO', output: 'Hello'
export function capitalizeOnlyFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const convertCamelToTitleCase = string => {
  if (!string) { return '' }

  return string
  // insert space before all caps
    .replace(/([A-Z])/g, ' $1')
  // uppercase the first character
    .replace(/^./, str => str.toUpperCase())
}

// 'not verified' => 'notVerified'
export const camelize = str => str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
  if (+match === 0) return '' // or if (/\s+/.test(match)) for white spaces
  return index === 0 ? match.toLowerCase() : match.toUpperCase()
})

// strings with spaces -> kebab
export const kebabize = str => {
  let ing = str.trim()
  ing = ing.replace(/[\s.]+/g, '-')
  ing = ing.toLowerCase()
  return ing
}

// https://stackoverflow.com/a/9462382
export const formatLongNumber = num => {
  const digits = 2
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ]
  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/
  const item = [...lookup].reverse().find(i => num >= i.value)
  return item ? (num / item.value).toFixed(digits).replace(regexp, '').concat(item.symbol) : '0'
}

export const firstLast4 = string => `${string.slice(0, 4)}...${string.slice(-4)}`

export const first20 = string => {
  if (string.length <= 20) {
    return string
  }
  return `${string.slice(0, 20)}...`
}

export const stringHasNumber = string => /\d/.test(string)
export const stringHasSymbol = string => /[-+_!@#$%^&*.,?]/.test(string)
export const stringHasLowerCase = string => string && /[a-z]/.test(string)
export const stringHasUpperCase = string => /[A-Z]/.test(string)

// binary not decimal
export function formatBytes(bytes, decimals = 2) {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`
}

export const normalizeIdentityBlob = identity => {
  const clone = _.cloneDeep(identity)
  if (_.isArray(clone)) {
    return clone.map(normalizeIdentityBlob)
  }
  if (_.isPlainObject(clone)) {
    return _.mapValues(clone, value => normalizeIdentityBlob(value))
  }
  const isEmptyString = _.isString(clone) && _.isEmpty(clone.trim())
  const isUndefined = _.isUndefined(clone)
  if (isEmptyString || isUndefined) {
    return null
  }
  return clone
}
